import type {
  ActiveConnection,
  ConnectedNode,
  ConnectionStatus,
  CredentialHealth,
  DashboardSummary,
  DashboardRecommendation,
  DashboardSession,
  DiagnosticsEndpoint,
  EventIngestHealth,
  RecentEvent,
  RelayEndpoint,
  RelayPair,
  RuntimeVersion,
  RelaySignals,
  StickyAssignment,
  NodeCategory,
  NodeStatus,
  StatusPanel,
  SummaryStat
} from "../utils/dashboard-types"
import { dashboardEnvironment } from "../utils/env"

export interface DashboardApiSnapshot {
  connections: ActiveConnection[]
  credentialHealth: CredentialHealth
  diagnosticsEndpoints: DiagnosticsEndpoint[]
  eventIngest: EventIngestHealth
  events: RecentEvent[]
  loadedAt: number
  nodes: ConnectedNode[]
  recommendations: DashboardRecommendation[]
  relayEndpoints: RelayEndpoint[]
  relayPairs: RelayPair[]
  relaySignals: RelaySignals
  sessions: DashboardSession[]
  stickyAssignments: StickyAssignment[]
  summary: DashboardSummary
  workerVersion: RuntimeVersion | null
}

export interface DashboardApiClient {
  createDashboardSession(dashboardPassword: string): Promise<DashboardAuthSession>
  openDashboardEventStream(
    dashboardToken: string | undefined,
    handlers: DashboardEventStreamHandlers
  ): DashboardEventStreamConnection
  runMaintenanceAction(action: DashboardMaintenanceAction, dashboardToken?: string): Promise<DashboardMaintenanceResult>
  loadDashboardSnapshot(dashboardToken?: string): Promise<DashboardApiSnapshot>
}

export interface DashboardApiClientOptions {
  apiBaseUrl?: string
  fetch?: typeof fetch
  timeoutMs?: number
  webSocket?: DashboardWebSocketConstructor
}

export interface DashboardAuthSession {
  expiresAt: number
  issuedAt: number
  token: string
  tokenType: "Bearer"
}

export type DashboardMaintenanceAction = "clear-cache" | "clear-offline-nodes" | "clear-session-credentials"

export interface DashboardMaintenanceResult {
  clearedAt: number
  d1Available?: boolean
  deletedKeys?: string[]
  deletedNodeIds?: string[]
  tables?: string[]
}

export interface DashboardEventStreamConnection {
  close(): void
  readonly readyState: number
}

export interface DashboardEventStreamHandlers {
  onClose?: (event: CloseEvent) => void
  onError?: (event: Event | Error) => void
  onMessage?: (message: DashboardEventStreamMessage) => void
  onOpen?: () => void
}

export type DashboardEventStreamMessage =
  | {
      connectedClients: number
      publishedAt: number
      sequence: number | null
      type: "dashboard.connected"
    }
  | {
      generatedAt: number
      publishedAt: number
      reason: string
      scopes: string[]
      sequence: number
      type: "dashboard.updated"
    }
  | {
      publishedAt: number
      sequence: number | null
      type: "dashboard.pong"
    }

export class DashboardApiError extends Error {
  readonly status: number | undefined
  readonly url: string

  constructor(message: string, options: { status?: number; url: string }) {
    super(message)
    this.name = "DashboardApiError"
    this.status = options.status
    this.url = options.url
  }
}

export function isDashboardApiError(value: unknown): value is DashboardApiError {
  return value instanceof DashboardApiError
}

const endpoints = {
  adminClearCache: "/v1/dashboard/admin/cache/clear",
  adminClearOfflineNodes: "/v1/dashboard/admin/nodes/offline/clear",
  adminClearSessionCredentials: "/v1/dashboard/admin/session-credentials/clear",
  authLogin: "/v1/dashboard/auth/login",
  connections: "/v1/data/connections.json",
  diagnostics: "/v1/data/diagnostics.json",
  events: "/v1/data/events.json",
  nodes: "/v1/data/nodes.json",
  recommendations: "/v1/data/recommendations.json",
  relay: "/v1/data/relay.json",
  sessions: "/v1/data/sessions.json",
  stream: "/v1/dashboard/events",
  snapshot: "/v1/data/snapshot",
  summary: "/v1/data/summary"
}

const dashboardEventsWebSocketProtocol = "undetect.dashboard.events.v1"
const webSocketClosedState = 3

type DashboardWebSocketConstructor = new (url: string | URL, protocols?: string | string[]) => WebSocket

export function useDashboardApi(options: DashboardApiClientOptions = {}): DashboardApiClient {
  const apiBaseUrl = options.apiBaseUrl ?? dashboardEnvironment.apiBaseUrl
  const fetcher = options.fetch ?? globalThis.fetch
  const timeoutMs = options.timeoutMs ?? 15000
  const webSocketFactory = options.webSocket ?? globalThis.WebSocket

  return {
    async createDashboardSession(dashboardPassword: string): Promise<DashboardAuthSession> {
      if (!fetcher) {
        throw new Error("Dashboard API fetch is unavailable in this runtime.")
      }

      const payload = await postJson(
        fetcher,
        dashboardUrl(endpoints.authLogin, apiBaseUrl),
        {
          dashboardPassword
        },
        timeoutMs
      )
      return normalizeDashboardAuthSession(payload)
    },
    openDashboardEventStream(
      dashboardToken: string | undefined,
      handlers: DashboardEventStreamHandlers
    ): DashboardEventStreamConnection {
      return openDashboardEventStream(webSocketFactory, dashboardWebSocketUrl(endpoints.stream, apiBaseUrl), {
        dashboardToken,
        handlers
      })
    },
    async runMaintenanceAction(
      action: DashboardMaintenanceAction,
      dashboardToken?: string
    ): Promise<DashboardMaintenanceResult> {
      if (!fetcher) {
        throw new Error("Dashboard API fetch is unavailable in this runtime.")
      }

      const path = maintenanceActionPath(action)
      const payload = await postJson(fetcher, dashboardUrl(path, apiBaseUrl), undefined, timeoutMs, dashboardToken)
      return normalizeDashboardMaintenanceResult(payload)
    },
    async loadDashboardSnapshot(dashboardToken?: string): Promise<DashboardApiSnapshot> {
      if (!fetcher) {
        throw new Error("Dashboard API fetch is unavailable in this runtime.")
      }

      const snapshot = await requestJson(
        fetcher,
        dashboardUrl(endpoints.snapshot, apiBaseUrl),
        dashboardToken,
        timeoutMs
      )
      return normalizeDashboardSnapshot(snapshot)
    }
  }
}

function maintenanceActionPath(action: DashboardMaintenanceAction): string {
  if (action === "clear-cache") {
    return endpoints.adminClearCache
  }
  if (action === "clear-offline-nodes") {
    return endpoints.adminClearOfflineNodes
  }
  return endpoints.adminClearSessionCredentials
}

function dashboardUrl(path: string, apiBaseUrl: string): string {
  if (!apiBaseUrl) {
    return path
  }

  return new URL(path, `${apiBaseUrl}/`).toString()
}

function dashboardWebSocketUrl(path: string, apiBaseUrl: string): string {
  const baseUrl =
    apiBaseUrl ||
    (typeof window !== "undefined" && window.location.href ? window.location.href : "http://dashboard.local/")
  const url = new URL(path, `${baseUrl.replace(/\/+$/, "")}/`)

  if (url.protocol === "http:") {
    url.protocol = "ws:"
  } else if (url.protocol === "https:") {
    url.protocol = "wss:"
  }

  return url.toString()
}

function openDashboardEventStream(
  webSocketFactory: DashboardWebSocketConstructor | undefined,
  url: string,
  options: {
    dashboardToken: string | undefined
    handlers: DashboardEventStreamHandlers
  }
): DashboardEventStreamConnection {
  if (!webSocketFactory) {
    options.handlers.onError?.(new Error("Dashboard event stream WebSocket is unavailable in this runtime."))
    return {
      close() {},
      readyState: webSocketClosedState
    }
  }

  const protocols = dashboardEventStreamProtocols(options.dashboardToken)
  const socket = new webSocketFactory(url, protocols)

  socket.addEventListener("open", () => {
    options.handlers.onOpen?.()
  })
  socket.addEventListener("message", (event) => {
    const parsed = parseDashboardEventStreamMessage(event.data)
    if (parsed) {
      options.handlers.onMessage?.(parsed)
    }
  })
  socket.addEventListener("close", (event) => {
    options.handlers.onClose?.(event)
  })
  socket.addEventListener("error", (event) => {
    options.handlers.onError?.(event)
  })

  return socket
}

function dashboardEventStreamProtocols(dashboardToken: string | undefined): string[] {
  const trimmedToken = dashboardToken?.trim()
  return trimmedToken ? [dashboardEventsWebSocketProtocol, trimmedToken] : [dashboardEventsWebSocketProtocol]
}

function parseDashboardEventStreamMessage(data: unknown): DashboardEventStreamMessage | null {
  if (typeof data !== "string") {
    return null
  }

  try {
    return normalizeDashboardEventStreamMessage(JSON.parse(data))
  } catch {
    return null
  }
}

function normalizeDashboardEventStreamMessage(value: unknown): DashboardEventStreamMessage | null {
  const record = asRecord(value)
  const type = readString(record?.type)
  const publishedAt = readNumber(record?.publishedAt) ?? Date.now()
  const sequence = readNumber(record?.sequence) ?? null

  if (type === "dashboard.connected") {
    return {
      connectedClients: readNumber(record?.connectedClients) ?? 0,
      publishedAt,
      sequence,
      type
    }
  }

  if (type === "dashboard.updated") {
    const reason = readString(record?.reason)
    const generatedAt = readNumber(record?.generatedAt)
    if (!reason || generatedAt === undefined || sequence === null) {
      return null
    }

    return {
      generatedAt,
      publishedAt,
      reason,
      scopes: arrayFromRecord(record, "scopes").map(readString).filter(isPresent),
      sequence,
      type
    }
  }

  if (type === "dashboard.pong") {
    return {
      publishedAt,
      sequence,
      type
    }
  }

  return null
}

async function postJson(
  fetcher: typeof fetch,
  url: string,
  body: unknown,
  timeoutMs: number,
  dashboardToken?: string
): Promise<unknown> {
  const controller = new AbortController()
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
    const trimmedToken = dashboardToken?.trim()
    if (trimmedToken) {
      headers.Authorization = `Bearer ${trimmedToken}`
    }
    const response = await fetcher(url, {
      body: body === undefined ? undefined : JSON.stringify(body),
      headers,
      method: "POST",
      signal: controller.signal
    })

    if (!response.ok) {
      throw new DashboardApiError(`Dashboard API request failed with ${response.status}.`, {
        status: response.status,
        url
      })
    }

    const contentType = response.headers.get("content-type") ?? ""
    if (!contentType.toLowerCase().includes("application/json")) {
      throw new DashboardApiError("Dashboard API response was not JSON.", {
        url
      })
    }

    return response.json()
  } finally {
    globalThis.clearTimeout(timeout)
  }
}

async function requestJson(
  fetcher: typeof fetch,
  url: string,
  dashboardToken: string | undefined,
  timeoutMs: number
): Promise<unknown> {
  const controller = new AbortController()
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetcher(url, {
      headers: dashboardHeaders(dashboardToken),
      signal: controller.signal
    })

    if (!response.ok) {
      throw new DashboardApiError(`Dashboard API request failed with ${response.status}.`, {
        status: response.status,
        url
      })
    }

    const contentType = response.headers.get("content-type") ?? ""
    if (!contentType.toLowerCase().includes("application/json")) {
      throw new DashboardApiError("Dashboard API response was not JSON.", {
        url
      })
    }

    return response.json()
  } finally {
    globalThis.clearTimeout(timeout)
  }
}

function normalizeDashboardMaintenanceResult(payload: unknown): DashboardMaintenanceResult {
  const record = asRecord(unwrapData(payload))
  const clearedAt = readNumber(record?.clearedAt)

  if (clearedAt === undefined) {
    throw new Error("Dashboard maintenance response is not valid.")
  }

  return {
    clearedAt,
    d1Available: readBoolean(record?.d1Available),
    deletedKeys: arrayFromRecord(record, "deletedKeys").map(readString).filter(isPresent),
    deletedNodeIds: arrayFromRecord(record, "deletedNodeIds").map(readString).filter(isPresent),
    tables: arrayFromRecord(record, "tables").map(readString).filter(isPresent)
  }
}

function dashboardHeaders(dashboardToken: string | undefined): HeadersInit {
  const headers: Record<string, string> = {
    Accept: "application/json"
  }
  const trimmedToken = dashboardToken?.trim()

  if (trimmedToken) {
    headers.Authorization = `Bearer ${trimmedToken}`
  }

  return headers
}

function normalizeDashboardAuthSession(payload: unknown): DashboardAuthSession {
  const record = asRecord(unwrapData(payload))
  const token = readString(record?.token)
  const tokenType = readString(record?.tokenType)
  const issuedAt = readNumber(record?.issuedAt)
  const expiresAt = readNumber(record?.expiresAt)

  if (!token || tokenType !== "Bearer" || issuedAt === undefined || expiresAt === undefined || expiresAt <= issuedAt) {
    throw new Error("Dashboard login response is not a valid token session.")
  }

  return {
    expiresAt,
    issuedAt,
    token,
    tokenType
  }
}

function normalizeDashboardSnapshot(payload: unknown): DashboardApiSnapshot {
  const data = unwrapData(payload)
  const record = asRecord(data)
  if (!record) {
    throw new Error("Dashboard snapshot response is not an object.")
  }

  const sessions = normalizeSessionsSnapshot(record)
  const relay = normalizeRelaySnapshot(record.relay)
  const events = normalizeEventsSnapshot(record)

  return {
    connections: normalizeConnections(record),
    credentialHealth: sessions.credentialHealth,
    diagnosticsEndpoints: normalizeDiagnostics(record.diagnostics),
    eventIngest: events.eventIngest,
    events: events.events,
    loadedAt: Date.now(),
    nodes: normalizeNodes(record),
    recommendations: normalizeRecommendations(record),
    relayEndpoints: relay.endpoints,
    relayPairs: relay.pairs,
    relaySignals: relay.signals,
    sessions: sessions.sessions,
    stickyAssignments: sessions.stickyAssignments,
    summary: normalizeSummary(record),
    workerVersion: normalizeWorkerVersion(record.diagnostics)
  }
}

function normalizeSummary(payload: unknown): DashboardSummary {
  const candidate = summaryCandidate(payload)
  if (isDashboardSummary(candidate)) {
    return candidate
  }

  const record = asRecord(candidate)
  if (!record) {
    throw new Error("Dashboard summary response is not an object.")
  }

  const generatedAt = readNumber(record.generatedAt) ?? Date.now()
  const activeNodes = readNumber(record.activeNodes) ?? 0
  const staleNodes = readNumber(record.staleNodes) ?? 0
  const blockedNodes = readNumber(record.blockedNodes) ?? 0
  const activeConnections = readNumber(record.activeConnections) ?? 0
  const pausedConnections = readNumber(record.pausedConnections) ?? 0
  const errorEvents = readNumber(record.errorEvents) ?? 0
  const throughputBytesPerSecond = readNumber(record.throughputBytesPerSecond) ?? 0

  return {
    generatedAt,
    panels: [
      panel("nodes", "Node health", `${activeNodes} active`, `${staleNodes} stale and ${blockedNodes} blocked`, 88),
      panel("connections", "Connection load", `${activeConnections} active`, `${pausedConnections} paused`, 74),
      panel(
        "throughput",
        "Throughput",
        `${Math.round(throughputBytesPerSecond / 1024)} KB/s`,
        `${errorEvents} recent error events`,
        52
      )
    ],
    recentEvents: [],
    stats: [
      stat("active-nodes", "Active nodes", String(activeNodes), "Ready for broker assignments", `${staleNodes} stale`),
      stat(
        "active-connections",
        "Active connections",
        String(activeConnections),
        "Relay-backed client flows",
        `${pausedConnections} paused`
      ),
      stat("blocked-nodes", "Blocked nodes", String(blockedNodes), "Unavailable for assignments", "Control policy"),
      stat(
        "throughput",
        "Throughput",
        `${Math.round(throughputBytesPerSecond / 1024)} KB/s`,
        "Across active sessions",
        `${errorEvents} errors`
      )
    ]
  }
}

function summaryCandidate(payload: unknown): unknown {
  const data = unwrapData(payload)
  const record = asRecord(data)
  return record?.summary ?? data
}

function normalizeNodes(payload: unknown): ConnectedNode[] {
  return arrayCandidate(payload, "nodes")
    .map(normalizeNode)
    .filter((node): node is ConnectedNode => node !== null)
}

function normalizeNode(value: unknown): ConnectedNode | null {
  const record = asRecord(value)
  if (!record) {
    return null
  }

  const id = readString(record.id) ?? readString(record.nodeId)
  if (!id) {
    return null
  }

  const category = parseNodeCategory(readString(record.nodeCategory))
  if (!category) {
    return null
  }
  const state = readString(record.status) ?? readString(record.currentState)
  const country = readString(record.country) ?? null

  return {
    activeClients: readNumber(record.activeClients) ?? null,
    activeChannels: readNumber(record.activeChannels) ?? null,
    acceptingClients: readBoolean(record.acceptingClients) ?? true,
    asn: readString(record.asn) ?? null,
    capacityReason: readString(record.capacityReason) ?? null,
    charging: readBoolean(record.charging) ?? null,
    city: readString(record.city) ?? null,
    country,
    deviceId: readString(record.deviceId) ?? undefined,
    id,
    ipFamily: parseIpFamily(readString(record.ipFamily)) ?? null,
    isp: readString(record.isp) ?? null,
    lastSeenAt: readNumber(record.lastSeenAt) ?? Date.now(),
    latencyMs: readNumber(record.latencyMs) ?? null,
    maxClients: readNumber(record.maxClients) ?? readNumber(record.maxClientCount) ?? 5,
    nodeCategory: category,
    observedIpAddress: readString(record.observedIpAddress),
    regionCode: readString(record.regionCode) ?? null,
    relayRegion: readString(record.relayRegion) ?? null,
    sdkVersion: readString(record.sdkVersion) ?? null,
    status: parseNodeStatus(state),
    throughputMbps: readNumber(record.throughputMbps) ?? null,
    timezone: readString(record.timezone) ?? null,
    transport: parseTransport(readString(record.transport)) ?? null
  }
}

function normalizeConnections(payload: unknown): ActiveConnection[] {
  return arrayCandidate(payload, "connections")
    .map(normalizeConnection)
    .filter((connection): connection is ActiveConnection => connection !== null)
}

function normalizeConnection(value: unknown): ActiveConnection | null {
  const record = asRecord(value)
  if (!record) {
    return null
  }

  const id = readString(record.id) ?? readString(record.connectionId)
  if (!id) {
    return null
  }

  const state = readString(record.status) ?? readString(record.state)

  return {
    clientId: readString(record.clientId) ?? "not-emitted",
    country: readString(record.country) ?? null,
    durationSeconds: readNumber(record.durationSeconds) ?? null,
    edgeRegion: readString(record.edgeRegion) ?? null,
    id,
    lastEventAt: readNumber(record.lastEventAt),
    lastEventType: readString(record.lastEventType),
    nodeId: readString(record.nodeId) ?? null,
    protocol: parseProtocol(readString(record.protocol)) ?? null,
    rxBytes: readNumber(record.rxBytes) ?? readNumber(record.bytesDown) ?? null,
    sessionId: readString(record.sessionId) ?? "not-emitted",
    startedAt: readNumber(record.startedAt) ?? null,
    status: parseConnectionStatus(state),
    sticky: readBoolean(record.sticky) ?? readBoolean(record.stickyMatched) ?? null,
    strict: readBoolean(record.strict) ?? null,
    target: readString(record.target) ?? null,
    txBytes: readNumber(record.txBytes) ?? readNumber(record.bytesUp) ?? null
  }
}

function normalizeSessionsSnapshot(payload: unknown): {
  credentialHealth: CredentialHealth
  sessions: DashboardSession[]
  stickyAssignments: StickyAssignment[]
} {
  const data = unwrapData(payload)
  const record = asRecord(data)
  return {
    credentialHealth: normalizeCredentialHealth(record?.credentialHealth),
    sessions: arrayFromRecord(record, "sessions").map(normalizeSession).filter(isPresent),
    stickyAssignments: arrayFromRecord(record, "stickyAssignments").map(normalizeStickyAssignment).filter(isPresent)
  }
}

function normalizeSession(value: unknown): DashboardSession | null {
  const record = asRecord(value)
  const id = readString(record?.id) ?? readString(record?.sessionId)
  const appId = readString(record?.appId)
  if (!record || !id || !appId) {
    return null
  }

  return {
    activeCredentialKid: readString(record.activeCredentialKid) ?? readString(record.kid) ?? "not-emitted",
    appId,
    closedAt: readNumber(record.closedAt),
    createdAt: readNumber(record.createdAt) ?? Date.now(),
    credentialExpiresAt: readEpochMillis(record.credentialExpiresAt) ?? null,
    id,
    lastSeenAt: readNumber(record.lastSeenAt) ?? Date.now(),
    previousKidGraceSeconds: readNumber(record.previousKidGraceSeconds) ?? 0,
    requestedCountry: readString(record.requestedCountry) ?? readString(record.country) ?? null,
    requestedProfile: readString(record.requestedProfile),
    status: parseSessionStatus(readString(record.status)),
    strict: readBoolean(record.strict) ?? false,
    stickyHashPresent: readBoolean(record.stickyHashPresent) ?? false
  }
}

function normalizeCredentialHealth(value: unknown): CredentialHealth {
  const record = asRecord(value)
  return {
    activeCredentials: readNumber(record?.activeCredentials) ?? 0,
    earliestExpiresAt: readEpochMillis(record?.earliestExpiresAt) ?? null,
    expiringWithin20Minutes: readNumber(record?.expiringWithin20Minutes) ?? 0,
    refreshEvents: readNumber(record?.refreshEvents) ?? 0,
    revokedCredentials: readNumber(record?.revokedCredentials) ?? 0,
    totalCredentials: readNumber(record?.totalCredentials) ?? 0
  }
}

function normalizeStickyAssignment(value: unknown): StickyAssignment | null {
  const record = asRecord(value)
  const id = readString(record?.id)
  const nodeId = readString(record?.nodeId)
  const sessionId = readString(record?.sessionId)
  if (!record || !id || !nodeId || !sessionId) {
    return null
  }

  return {
    fallbackReason: readString(record.fallbackReason) ?? "",
    id,
    nodeId,
    requestedCountry: readString(record.requestedCountry) ?? null,
    selectedReason: readString(record.selectedReason) ?? "Sticky mapping is present.",
    sessionId,
    strictFailureReason: readString(record.strictFailureReason) ?? "",
    stickyState: parseStickyState(readString(record.stickyState)),
    updatedAt: readNumber(record.updatedAt)
  }
}

function normalizeRelaySnapshot(payload: unknown): {
  endpoints: RelayEndpoint[]
  pairs: RelayPair[]
  signals: RelaySignals
} {
  const data = unwrapData(payload)
  const record = asRecord(data)
  const signalsRecord = asRecord(record?.signals)
  return {
    endpoints: arrayFromRecord(record, "endpoints").map(normalizeRelayEndpoint).filter(isPresent),
    pairs: arrayFromRecord(record, "pairs").map(normalizeRelayPair).filter(isPresent),
    signals: {
      eventCount: readNumber(signalsRecord?.eventCount) ?? 0,
      missing: arrayFromRecord(signalsRecord, "missing")
        .map((item) => readString(item))
        .filter(isPresent)
    }
  }
}

function normalizeRelayEndpoint(value: unknown): RelayEndpoint | null {
  const record = asRecord(value)
  const id = readString(record?.id)
  const region = readString(record?.region)
  if (!record || !id || !region) {
    return null
  }

  return {
    cloud: readString(record.cloud) ?? null,
    datagramsPerSecond: readNumber(record.datagramsPerSecond) ?? null,
    healthPort: readNumber(record.healthPort) ?? null,
    host: readString(record.host),
    id,
    listenPort: readNumber(record.listenPort) ?? null,
    maxDatagramBytes: readNumber(record.maxDatagramBytes) ?? null,
    region,
    sessionTtlSeconds: readNumber(record.sessionTtlSeconds) ?? null,
    status: parseRelayStatus(readString(record.status))
  }
}

function normalizeRelayPair(value: unknown): RelayPair | null {
  const record = asRecord(value)
  const sessionId = readString(record?.sessionId)
  if (!record || !sessionId) {
    return null
  }

  return {
    androidState: parseParticipantState(readString(record.androidState)),
    browserState: parseParticipantState(readString(record.browserState)),
    closeReason: readString(record.closeReason) ?? "not emitted",
    forwardErrors: readNumber(record.forwardErrors) ?? null,
    idleSeconds: readNumber(record.idleSeconds) ?? 0,
    latencyMs: readNumber(record.latencyMs) ?? null,
    lossPercent: readNumber(record.lossPercent) ?? null,
    receivedBytes: readNumber(record.receivedBytes) ?? null,
    relayRegion: readString(record.relayRegion) ?? "not-emitted",
    sentBytes: readNumber(record.sentBytes) ?? null,
    sessionId,
    status: parseRelayPairStatus(readString(record.status))
  }
}

function normalizeEventsSnapshot(payload: unknown): { eventIngest: EventIngestHealth; events: RecentEvent[] } {
  const data = unwrapData(payload)
  const record = asRecord(data)
  return {
    eventIngest: normalizeEventIngest(record?.eventIngest),
    events: arrayFromRecord(record, "events").map(normalizeEvent).filter(isPresent)
  }
}

function normalizeEvent(value: unknown): RecentEvent | null {
  const record = asRecord(value)
  const id = readString(record?.id)
  const component = readString(record?.component)
  const message = readString(record?.message)
  if (!record || !id || !component || !message) {
    return null
  }

  return {
    clientId: readString(record.clientId),
    component,
    id,
    level: parseEventLevel(readString(record.level)),
    message,
    nodeId: readString(record.nodeId),
    sessionId: readString(record.sessionId),
    timestamp: readNumber(record.timestamp) ?? Date.now(),
    type: readString(record.type)
  }
}

function normalizeEventIngest(value: unknown): EventIngestHealth {
  const record = asRecord(value)
  return {
    acceptedBatches: readNumber(record?.acceptedBatches) ?? 0,
    duplicateBatches: readNumber(record?.duplicateBatches) ?? null,
    hardEvents: readNumber(record?.hardEvents) ?? 0,
    rejectedBatches: readNumber(record?.rejectedBatches) ?? null,
    sanitizationDropCount: readNumber(record?.sanitizationDropCount) ?? null,
    storedEvents: readNumber(record?.storedEvents) ?? 0
  }
}

function normalizeRecommendations(payload: unknown): DashboardRecommendation[] {
  return arrayCandidate(payload, "recommendations").map(normalizeRecommendation).filter(isPresent)
}

function normalizeRecommendation(value: unknown): DashboardRecommendation | null {
  const record = asRecord(value)
  const id = readString(record?.id)
  const title = readString(record?.title)
  if (!record || !id || !title) {
    return null
  }

  return {
    affectedCount: readNumber(record.affectedCount) ?? 0,
    evidence: readString(record.evidence) ?? "Evidence was not emitted.",
    id,
    nextAction: readString(record.nextAction) ?? "Inspect the related dashboard surface.",
    reason: readString(record.reason) ?? "The dashboard has incomplete data for this area.",
    scope: readString(record.scope) ?? "dashboard",
    severity: parseRecommendationSeverity(readString(record.severity)),
    title
  }
}

function normalizeDiagnostics(payload: unknown): DiagnosticsEndpoint[] {
  return arrayCandidate(payload, "endpoints").map(normalizeDiagnosticsEndpoint).filter(isPresent)
}

function normalizeWorkerVersion(payload: unknown): RuntimeVersion | null {
  const diagnostics = asRecord(unwrapData(payload))
  const versions = asRecord(diagnostics?.versions)
  return normalizeRuntimeVersion(versions?.worker)
}

function normalizeRuntimeVersion(value: unknown): RuntimeVersion | null {
  const record = asRecord(value)
  const component = readString(record?.component)
  const version = readString(record?.version)
  const major = readNumber(record?.major)
  const reportsAs = readString(record?.reportsAs)
  if (!record || !component || !version || major === undefined || !reportsAs) {
    return null
  }
  const build = asRecord(record.build)
  return {
    build: build
      ? {
          builtAt: readNullableString(build.builtAt),
          commit: readNullableString(build.commit),
          dirty: typeof build.dirty === "boolean" ? build.dirty : null
        }
      : undefined,
    compatibility: readStringRecord(record.compatibility),
    component,
    major,
    reportsAs,
    version
  }
}

function normalizeDiagnosticsEndpoint(value: unknown): DiagnosticsEndpoint | null {
  const record = asRecord(value)
  const id = readString(record?.id)
  const label = readString(record?.label)
  const path = readString(record?.path)
  if (!record || !id || !label || !path) {
    return null
  }

  return {
    id,
    label,
    path,
    status: parseDiagnosticsStatus(readString(record.status))
  }
}

function arrayCandidate(payload: unknown, key: string): unknown[] {
  const data = unwrapData(payload)
  if (Array.isArray(data)) {
    return data
  }

  const record = asRecord(data)
  const items = record?.[key]
  if (Array.isArray(items)) {
    return items
  }

  throw new Error(`Dashboard ${key} response is not an array.`)
}

function arrayFromRecord(record: Record<string, unknown> | undefined, key: string): unknown[] {
  const value = record?.[key]
  return Array.isArray(value) ? value : []
}

function unwrapData(payload: unknown): unknown {
  const record = asRecord(payload)
  return record && "data" in record ? record.data : payload
}

function isDashboardSummary(value: unknown): value is DashboardSummary {
  const record = asRecord(value)
  return (
    typeof record?.generatedAt === "number" &&
    Array.isArray(record.stats) &&
    Array.isArray(record.panels) &&
    Array.isArray(record.recentEvents)
  )
}

function stat(id: string, label: string, value: string, caption: string, trend: string): SummaryStat {
  return {
    caption,
    id,
    label,
    tone: "neutral",
    trend,
    value
  }
}

function panel(id: string, title: string, value: string, detail: string, progress: number): StatusPanel {
  return {
    detail,
    id,
    progress: clampProgress(progress),
    title,
    tone: "neutral",
    value
  }
}

function parseNodeStatus(value: string | undefined): NodeStatus {
  if (value === "active" || value === "available" || value === "connected") {
    return "available"
  }
  if (value === "disconnecting" || value === "draining") {
    return "draining"
  }
  if (value === "stale") {
    return "stale"
  }
  if (value === "probationary") {
    return "probationary"
  }
  if (value === "blocked") {
    return "blocked"
  }
  return "offline"
}

function parseConnectionStatus(value: string | undefined): ConnectionStatus {
  if (value === "active") {
    return "active"
  }
  if (value === "warming" || value === "resuming") {
    return "warming"
  }
  return "paused"
}

function parseProtocol(value: string | undefined): ActiveConnection["protocol"] | undefined {
  if (value === "tcp" || value === "udp") {
    return value
  }
  return undefined
}

function parseSessionStatus(value: string | undefined): DashboardSession["status"] | undefined {
  if (value === "active" || value === "closed") {
    return value
  }
  return undefined
}

function parseStickyState(value: string | undefined): StickyAssignment["stickyState"] {
  if (value === "miss" || value === "fallback") {
    return value
  }
  return "hit"
}

function parseRelayStatus(value: string | undefined): RelayEndpoint["status"] {
  if (value === "healthy" || value === "degraded" || value === "offline" || value === "configured") {
    return value
  }
  return "configured"
}

function parseParticipantState(value: string | undefined): RelayPair["androidState"] {
  if (value === "missing" || value === "replaced") {
    return value
  }
  return "connected"
}

function parseRelayPairStatus(value: string | undefined): RelayPair["status"] {
  if (value === "unpaired" || value === "idle-expiring") {
    return value
  }
  return "paired"
}

function parseEventLevel(value: string | undefined): RecentEvent["level"] {
  if (value === "warn") {
    return "warn"
  }
  if (value === "error") {
    return "error"
  }
  return "info"
}

function parseRecommendationSeverity(value: string | undefined): DashboardRecommendation["severity"] {
  if (value === "high" || value === "medium" || value === "low") {
    return value
  }
  return "low"
}

function parseDiagnosticsStatus(value: string | undefined): DiagnosticsEndpoint["status"] {
  if (value === "ready" || value === "planned" || value === "fallback") {
    return value
  }
  return "planned"
}

function parseNodeCategory(value: string | undefined): NodeCategory | undefined {
  if (value === "android-sdk" || value === "residential-isp" || value === "vps") {
    return value
  }
  return undefined
}

function parseIpFamily(value: string | undefined): ConnectedNode["ipFamily"] | undefined {
  if (value === "IPv4" || value === "IPv6" || value === "Dual") {
    return value
  }
  return undefined
}

function parseTransport(value: string | undefined): ConnectedNode["transport"] | undefined {
  if (value === "relay-masque") {
    return value
  }
  return undefined
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function readEpochMillis(value: unknown): number | undefined {
  const timestamp = readNumber(value)
  return timestamp !== undefined && timestamp > 0 ? timestamp : undefined
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined
}

function readNullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null
}

function readStringRecord(value: unknown): Record<string, string> {
  const record = asRecord(value)
  if (!record) {
    return {}
  }
  return Object.fromEntries(
    Object.entries(record).filter((entry): entry is [string, string] => typeof entry[1] === "string")
  )
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

function clampProgress(value: number): number {
  return Math.max(0, Math.min(100, value))
}
